/* eslint-disable jsx-control-statements/jsx-jcs-no-undef */
/**
 * 多语言统一管理
 */
import {
  DEFAULT_LOCALE,
  LOCALE_LS_KEY,
  I18N_URL,
  LOCALES,
} from './config/i18n-config';
import { env } from './env';
import { SYSTEM_SYMBOL } from './env';
/**
 * 判断语言是否支持
 * @param lang 需要判断的语言
 * @returns 支持返回true
 */
export const supportLang = (lang: string): boolean => {
  return LOCALES.map((v) => v.value).includes(lang);
};

/**
 * 替换链接中的语言标识
 *
 * @description
 * 1. 会自动替换错误的
 * 2. 根据当前的语言，自动处理前缀
 * 3. 如果为单语言，将自动删除 lang 前缀
 *
 * @param  path - 路径链接.
 * @param  [lang=i18n.global.locale.value] - 追加语言，默认为当前语言
 * @return 替换后的路径
 *
 * @example
 *  1. 默认追加当前语言
 *      replace('/login') => /zh-cn/login
 *
 *  2. 替换当前语言
 *      replace('/en/login') => /zh-cn/login
 *
 *  3. 替换参数语言
 *      replace('/en/login', 'zh') => /zh/login
 *
 *  4. 自动修改链接中的 /
 *      replace('//zh-CN///login') => /zh-CN/login
 */
export const replacePath = (path: string, lang?: string): string => {
  if (!lang) lang = getLocale();
  // 过滤开头的 / ，用来做路径分隔。过滤转义符，防止外链接钓鱼
  // 以 / 分隔来取 lang
  const paths = path.replace(/^[\\/]+/, '').split(/\/+/);

  // 如果一级是语言，则删除
  if (supportLang(paths[0])) {
    paths.shift();
  }

  // 处理多语言前缀
  return lang ? `/${lang}/${paths.join('/')}` : `/${paths.join('/')}`;
};

/**
 * 获取除origin之外的全路径-替换语言之后的
 *
 * @param lang
 * @returns 全路径
 */
export const getFullPath = (lang: string): string => {
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname.replace(/[&<>"']/g, '');
    const fullPath = `${pathname}${window.location.search}${window.location.hash}`;

    return replacePath(fullPath, lang);
  }
  return '';
};

/**
 * 获取当前语言
 * @description 缓存语言 > 浏览器语言 > 默认语言
 * @returns locale
 */
export const getLocale = (): string => {
  /** 服务端渲染 */
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  const locale =
    getLocaleFromPathname(window.location.pathname) ||
    localStorage.getItem(LOCALE_LS_KEY) ||
    '';

  if (locale) {
    if (supportLang(locale)) {
      return locale;
    } else {
      return DEFAULT_LOCALE;
    }
  } else {
    // 以下为首次访问逻辑
    const { language } = navigator;
    const index = LOCALES.findIndex(
      (lang) => language.indexOf(lang.value.split('-')[0]) > -1
    );

    // 系统语言支持返回对应系统语言
    if (index > -1) {
      // console.log('index', index, LOCALES[index].value);
      return LOCALES[index].value;
    }
  }

  // 返回默认语言
  return DEFAULT_LOCALE;
};

/**
 * 设置语言
 * @param lang 设置的语言标识
 * @param refresh 是否需要刷新页面
 */
export const setLocale = (lang: string, refresh?: boolean) => {
  /** 服务端不设置 */
  if (!supportLang(lang) || typeof window === 'undefined') return;
  /** 设置缓存 */
  localStorage.setItem(LOCALE_LS_KEY, lang);
  // console.log('hello', getFullPath(lang));
  /** 根据是否刷新进行地址栏操作 */
  if (refresh) {
    window.location.href = getFullPath(lang);
  } else {
    window.history.pushState(history.state, '', getFullPath(lang));
    window.dispatchEvent(
      new PopStateEvent('popstate', { state: history.state })
    );
  }
};

const loadingMap = LOCALES.reduce(
  (res, val) => {
    return {
      ...res,
      [val.value]: false,
    };
  },
  {
    links: false,
  } as Record<string, boolean>
);

/**
 * 获取语言包
 */
let messageLinks: Record<string, string>;
export const getMessageLinks = async () => {
  if (loadingMap.links) return;
  loadingMap.links = true;
  const response = await fetch(
    `${I18N_URL}/web3-common/test-summary-link.json`
  );
  loadingMap.links = false;
  if (response.status === 200) {
    const { langHashLinks } = await response.json();
    messageLinks = langHashLinks;
  }
};

/**
 * 获取语言包
 * @param project i18n系统的项目名
 * @param lang		要加载的语言标识
 */
let message: { [key: string]: Record<string, string> } = {};
export const getMessage = async (project: string, lang: string) => {
  let fileName = `${lang}`;
  if (env === 'test' || env === 'dev') {
    fileName = `test-${lang}`;
  }
  if (env === 'testnet') {
    fileName = `testnet-${lang}`;
  }
  const response = await fetch(`${I18N_URL}/${project}/${fileName}.json`);
  loadingMap[lang] = false;
  if (response.status === 200) {
    const result = await response.json();
    message = {
      ...message,
      [lang]: result,
    };
    return message[lang];
  }
  return {};
};

const localeRegStr = '(en)|([a-z]{2,3}-[A-Z0-9]{2,3})';
const localeReg = new RegExp(`^/(${localeRegStr})/`);

/**
 * 当前用户状态或设置下的默认语言
 * TODO：可能是浏览器设置、IP等因素，暂时先就直接返回站点默认
 * @returns 默认语言
 */
export function getDefaultLocale(): string {
  return DEFAULT_LOCALE;
}

export function isSupportedLocale(locale = ''): boolean {
  return LOCALES.map((item) => item.value).includes(locale);
}

export function getLocaleFromPathname(pathname: string): string {
  return pathname.match(localeReg)?.[1] ?? '';
}

export function getCurrentLocale() {
  if (typeof window === 'undefined') {
    return '';
  }
  const locale = getLocaleFromPathname(window.location.pathname);
  return isSupportedLocale(locale) ? locale : '';
}

/**
 * 将地址一般化为可以用于导航的地址，
 * 例如：/collections 转换为：/当前语言/AppPath/collections
 * /AppPath/collections 转换为：/当前语言/AppPath/collections
 * /某语言/AppPath/
 *
 * @param to 目标地址
 */
export function normalizeLink(
  to: string,
  locale: string = '',
  AppPath = `/${SYSTEM_SYMBOL}`
): string {
  let fixedLocale = locale;
  if (!locale || !isSupportedLocale(locale)) {
    fixedLocale = getCurrentLocale() || getDefaultLocale();
  }

  if (to === '' || to === '/') {
    return `/${fixedLocale}${AppPath}`;
  }

  // todo locale to support locale types
  if (to.startsWith(AppPath) && to !== '/') {
    return `/${fixedLocale}${to}`;
  }
  const toLocale = getLocaleFromPathname(to);

  if (toLocale) {
    if (!locale && isSupportedLocale(toLocale)) {
      return to;
    }
    return to.replace(localeReg, `/${fixedLocale}/`);
  } else {
    return `/${fixedLocale}${AppPath}${to}`;
  }
}

export function normalizeOutLink(to: string, locale: string = '') {
  let fixedLocale = locale;
  if (!locale || !isSupportedLocale(locale)) {
    fixedLocale = getCurrentLocale() || getDefaultLocale();
  }

  if (to.startsWith('/')) {
    return `/${fixedLocale}${to}`;
  }

  return `/${fixedLocale}/${to}`;
}

export function buildAppPathReg(
  appBasePath: string,
  mode: 'withLang' | 'opt_lang' | 'no_lang' = 'opt_lang'
) {
  const appBase = appBasePath.startsWith('/') ? appBasePath : `/${appBasePath}`;
  if (mode === 'no_lang') {
    return new RegExp(`^${appBase}`);
  } else if (mode === 'withLang') {
    return new RegExp(`^/(${localeRegStr})(${appBase})`);
  } else if (mode === 'opt_lang') {
    return new RegExp(`^(/(${localeRegStr}))?(${appBase})`);
  }
}
